import {inject, Injectable} from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {Entity} from "../entity.interface";
import {DirtyDataWriterService} from "./dirty-data-writer.service";
import {PhotoService} from "../photo/photo.service";
import {LoggerService} from "../logging/logger.service";
import {ObservabilityService} from "../observability/observability.service";

@Injectable()
export class DirtyDataInterceptor implements HttpInterceptor {
  private readonly dirtyDataWriterService = inject(DirtyDataWriterService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService = inject(ObservabilityService);


  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logger.info(`DirtyDataInterceptor: Error occurred for request`)
          if (this.isConnectionError(error) &&
            this.isRetryableMethod(request.method) &&
            this.isAPIRequest(request.url)) {

            this.observabilityService.trackDataSyncInitialFailure(
              this.getRequestType(request.body),
              error,
              {
                url: request.url,
                method: request.method,
                errorStatus: error.status,
                isOffline: !navigator.onLine,
                requestType: this.getRequestType(request.body),
              }
            );

            this.logger.debug('Has a retryable request:', this.logger.stringify(request));
            // Store the failed request for later retry
            if (this.implementsEntity(request.body)) {
              const failedRequest = new FailedRequestEntity(request.url, request.method, request.body);
              this.dirtyDataWriterService.storeFailedRequest(
                failedRequest
              ).then(() => {
                this.observabilityService.trackDataSyncRequestStored(failedRequest.id, {
                  url: failedRequest.url,
                  method: failedRequest.method,
                  retries: failedRequest.retries,
                  type: this.getRequestType(request.body)
                });
                this.logger.debug('Request stored for retry');
              }).catch((storeError) => {
                this.observabilityService.captureException(storeError, {
                  context: 'DataSync.RequestStoreFailed',
                  url: request.url,
                  method: request.method,
                  body: request.body
                });
              });
              // return a successful response using the body of the request
              return of(new HttpResponse({status: 200, body: request.body}));

            } else if (request.body instanceof FormData) {
              let form : FormData = request.body;
              PhotoService.fromFormData(form).then((entity) => {
                const failedRequest = new FailedRequestEntity(request.url, request.method, entity);
                this.dirtyDataWriterService.storeFailedRequest(
                  failedRequest
                ).then(() => {
                  this.observabilityService.trackDataSyncRequestStored(failedRequest.id, {
                    url: failedRequest.url,
                    method: failedRequest.method,
                    retries: failedRequest.retries,
                    type: "photo"
                  });
                  this.logger.debug('Photo Request stored for retry');
                }).catch((storeError) => {
                  this.observabilityService.captureException(storeError, {
                    context: 'DataSync.PhotoStoreFailed',
                    url: request.url,
                    method: request.method
                  });
                });
              });
              return of(new HttpResponse({status: 200, body: request.body}));
            }
          }

          this.logger.info('Ignoring error, not a retryable request or not an API request');
          return throwError(() => error);

        }
      )
    );

  }

  private implementsEntity(obj: any): obj is Entity<any> {
    this.logger.debug('Checking entity:', this.logger.stringify(obj));
    return 'id' in obj && 'type' in obj;
  }

  /**
   * Determines if the request is an API request, excluding '/checklist' endpoints.
   * 
   * Checklist requests are excluded from API request handling because they are
   * processed differently elsewhere in the application (e.g., for offline support,
   * caching, or business logic reasons). This ensures that checklist-related
   * requests are not intercepted or handled by the dirty data mechanism.
   */
  private isAPIRequest(url: string): boolean {
    return url.includes('/api/') && !url.includes('/checklist');
  }

  private isConnectionError(error: HttpErrorResponse): boolean {
    return !navigator.onLine || error.status === 0; // Check if offline or status is 0 (connection error)
  }

  private isRetryableMethod(method: string): boolean {
    return ['POST', 'PUT', 'DELETE'].includes(method);
  }

  private getRequestType(body: any): string {
    if (body instanceof FormData) {
      return 'photo';
    } else if (this.implementsEntity(body)) {
      return body.type ?? 'entity';
    }
    return 'unknown';
  }

}

export class FailedRequestEntity implements Entity<string> {
  id : string;
  time: number = new Date().getTime();
  type : string;
  url : string;
  body : Entity<any> | FormData | null;
  method : string;
  retries : number = 0;
  startTime: Date = new Date();

  constructor(url : string, method : string, body : Entity<any> | FormData | null) {
    this.type = 'FailedRequestEntity';
    this.url = url;
    this.body = body;
    this.method = method;
    if (body) {
      this.id = this.hash(url + method + JSON.stringify(body));
    } else {
      this.id = this.hash(url + method);
    }
  }

  private hash(str : string) : string {
    let hash = 0, i, chr;
    if (str.length === 0) return hash + '';
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return hash + '';
  }

  incrementRetries() {
    this.retries = this.retries+ 1;
  }

  getAgeInSeconds(): number {
    return Math.floor((new Date().getTime() - this.startTime.getTime()) / 1000);
  }

}
